import React, { useState, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useAuth } from "./hooks/useAuth";
import { useNotifications } from "./hooks/useNotifications";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import { NotificationToast } from "./components/notifications/NotificationToast";
import { CallProvider } from "./contexts/CallContext";
import CallWidget from "./components/CallWidget";
import { AISupportProvider } from "./contexts/AISupportContext";
import AISupportWidget from "./components/AISupportWidget";
import AISupportConsoleScreen from "./screens/AISupportConsoleScreen";
import { LoginScreen } from "./screens/LoginScreen";
import { PatientsScreen } from "./screens/PatientsScreen";
import { AdHocMeetingsScreen } from "./screens/AdHocMeetingsScreen";
import { CalendarScreen } from "./screens/CalendarScreen";
import { TasksScreen } from "./screens/TasksScreen";
import { InternalTasksScreen } from "./screens/InternalTasksScreen";
import { UsersScreen } from "./screens/UsersScreen";
import { ConnectionsScreen } from "./screens/ConnectionsScreen";
import { WhatsAppScreen } from "./screens/WhatsAppScreen";
import { PatientPage } from "./screens/PatientPage";
import { GoogleCallbackScreen } from "./screens/GoogleCallbackScreen";
import { BillingScreen } from "./screens/BillingScreen";
import apiService from "./services/api";
import { MedicationType, ClinicUserApiResponse, MeetingType, DiagnosisCode, PatientFileType, GetClinicsResponse } from "./types/api";

//tr

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      retry: 1,
    },
  },
});

type Screen =
  | { type: "calendar" }
  | { type: "patients" }
  | { type: "adhoc-meetings" }
  | { type: "tasks" }
  | { type: "internal-tasks" }
  | { type: "users" }
  | { type: "connections" }
  | { type: "billing" }
  | { type: "ai-support" }
  | { type: "whatsapp"; phone?: string; patientId?: string | number }
  | { type: "patient"; id: string | number };

function App() {
  const auth = useAuth();
  const [isReady] = useState(true);
  const [users, setUsers] = useState<ClinicUserApiResponse[]>([]);
  const [medicationTypes, setMedicationTypes] = useState<MedicationType[]>([]);
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [diagnosisCodes, setDiagnosisCodes] = useState<DiagnosisCode[]>([]);
  const [patientFileTypes, setPatientFileTypes] = useState<PatientFileType[]>([]);
  const [clinics, setClinics] = useState<GetClinicsResponse>({ items: [] });
  const [taskActionCount, setTaskActionCount] = useState(0);
  const [whatsAppUnreadCount, setWhatsAppUnreadCount] = useState(0);
  // Bumped on each notification_count_changed SSE event; drives the
  // independent notification center to refetch.
  const [notificationRefreshSignal, setNotificationRefreshSignal] = useState(0);

  useNotifications(auth.isAuthenticated, {
    onTasksCount: (count) => setTaskActionCount(count),
    onWhatsAppUnread: (count) => setWhatsAppUnreadCount(count),
    onNotificationCountChanged: () => setNotificationRefreshSignal((s) => s + 1),
  });

  // Initialize screen from URL pathname and query params
  const getScreenFromPath = (): Screen => {
    const path = window.location.pathname.replace('/', '');
    const params = new URLSearchParams(window.location.search);

    // Check for patient page with ID
    if (path === 'patient' && params.has('id')) {
      return { type: 'patient', id: params.get('id')! };
    }

    // Handle regular screens
    if (path === 'patients') return { type: 'patients' };
    if (path === 'adhoc-meetings') return { type: 'adhoc-meetings' };
    if (path === 'calendar') return { type: 'calendar' };
    if (path === 'tasks') return { type: 'tasks' };
    if (path === 'internal-tasks') return { type: 'internal-tasks' };
    if (path === 'users') return { type: 'users' };
    if (path === 'connections') return { type: 'connections' };
    if (path === 'billing') return { type: 'billing' };
    if (path === 'ai-support') return { type: 'ai-support' };
    if (path === 'whatsapp') {
      return {
        type: 'whatsapp',
        phone: params.get('phone') || undefined,
        patientId: params.get('patient_id') || undefined,
      };
    }

    return { type: 'calendar' }; // default
  };

  const [currentScreen, setCurrentScreen] = useState<Screen>(getScreenFromPath);

  // Update URL when screen changes
  useEffect(() => {
    let path = '/';
    let search = '';

    if (currentScreen.type === 'patient') {
      path = '/patient';

      // Only update URL if the id parameter is different or path changed
      const currentParams = new URLSearchParams(window.location.search);
      const currentId = currentParams.get('id');

      if (window.location.pathname !== path || currentId !== currentScreen.id.toString()) {
        // Preserve all existing query parameters and only update the id
        currentParams.set('id', currentScreen.id.toString());
        search = `?${currentParams.toString()}`;
        const fullPath = path + search;
        window.history.pushState({}, '', fullPath);
        console.log(`[App] Updated URL to ${fullPath}`);
      }
      return; // Don't run the code below for patient pages
    } else if (currentScreen.type === 'whatsapp') {
      path = '/whatsapp';
      const waParams = new URLSearchParams();
      if (currentScreen.phone) waParams.set('phone', currentScreen.phone);
      if (currentScreen.patientId) waParams.set('patient_id', currentScreen.patientId.toString());
      search = waParams.toString() ? `?${waParams.toString()}` : '';
    } else {
      path = `/${currentScreen.type}`;
      // For non-patient pages, preserve view/date params but clear id and meeting_id
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.delete('id');
      currentParams.delete('meeting_id');
      search = currentParams.toString() ? `?${currentParams.toString()}` : '';
    }

    // For non-patient pages, update if path changed
    const fullPath = path + search;
    if (window.location.pathname + window.location.search !== fullPath) {
      window.history.pushState({}, '', fullPath);
    }
  }, [currentScreen]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      setCurrentScreen(getScreenFromPath());
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Check if we're processing a Google callback (has code in query params)
  // Use state to persist this across renders - once we're in callback mode, stay there
  const [isGoogleCallback] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has("code");
  });

  // Check if we're processing a forgot password flow (has key in query params on forgot_password path)
  const [forgotPasswordKey] = useState(() => {
    const path = window.location.pathname.replace('/', '');
    const params = new URLSearchParams(window.location.search);
    if (path === "forgot_password" && params.has("key")) {
      return params.get("key") || undefined;
    }
    return undefined;
  });

  // Track if we've already fetched the data to prevent duplicate calls
  const hasFetchedData = React.useRef(false);

  // Fetch users and medication types once when authenticated
  useEffect(() => {
    // Only fetch if authenticated and haven't fetched yet
    if (!auth.isAuthenticated || !auth.user || hasFetchedData.current) {
      return;
    }

    const fetchData = async () => {
      const token = localStorage.getItem("token");
      if (!token || !auth.user) return;

      try {
        // Fetch all data in parallel
        const [usersData, medTypesData, meetingTypesData, diagnosisData, fileTypesData, clinicsData] = await Promise.all([
          apiService.getUsers({
            token,
            user_type: "clinic",
            institution_id: auth.user.institution_id || 0,
          }),
          apiService.getMedicationTypes({ token }),
          apiService.getMeetingTypes(token, false), // false = not for filter
          apiService.getDiagnosis({ token }),
          apiService.getPatientFileTypes({ token, owner: 'patient' }),
          apiService.getClinics(token),
        ]);

        setUsers(usersData);
        setMedicationTypes(medTypesData);
        setMeetingTypes(meetingTypesData);
        setDiagnosisCodes(diagnosisData);
        setPatientFileTypes(fileTypesData);
        setClinics(clinicsData);
        hasFetchedData.current = true;

        // Fetch task action count
        try {
          const taskData = await apiService.getNumberOfTasksNeedAction({ token });
          setTaskActionCount(taskData.result);
        } catch {
          // Non-critical, silently ignore
        }
      } catch (error) {
        console.error("Failed to fetch initial data:", error);
      }
    };

    fetchData();
  }, [auth.isAuthenticated, auth.user]); // auth.user is needed as it's accessed in the effect

  // Function to refetch users (can be called after create/update)
  const refetchUsers = async () => {
    const token = localStorage.getItem("token");
    if (!token || !auth.user) return;

    try {
      const usersData = await apiService.getUsers({
        token,
        user_type: "clinic",
        institution_id: auth.user.institution_id || 0,
      });
      setUsers(usersData);
    } catch (error) {
      console.error("Failed to refetch users:", error);
    }
  };

  if (!isReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  const renderScreen = () => {
    if (!auth.isAuthenticated || !auth.user) return null;

    const handleNavigate = (screen: "calendar" | "patients" | "tasks" | "internal-tasks" | "patient-page" | "patient" | "users" | "connections" | "billing" | "whatsapp" | "adhoc-meetings" | "ai-support", patientId?: string | number, meetingId?: string | number, phone?: string) => {
      // Patient page menu item does nothing (handled in Sidebar)
      if (screen === "patient-page") {
        return;
      }

      if (screen === "ai-support") {
        setCurrentScreen({ type: "ai-support" });
        return;
      }

      if (screen === "whatsapp") {
        setCurrentScreen({ type: "whatsapp", phone, patientId });
        setWhatsAppUnreadCount(0); // clear badge immediately on navigate
        return;
      }

      if (screen === "patient" && patientId) {
        // If meeting_id is provided, add it to the URL params before navigation
        if (meetingId) {
          const params = new URLSearchParams();
          params.set('id', patientId.toString());
          params.set('meeting_id', meetingId.toString());
          window.history.pushState({}, '', `/patient?${params.toString()}`);
        }
        setCurrentScreen({ type: "patient", id: patientId });
      } else if (screen === "calendar" || screen === "patients" || screen === "adhoc-meetings" || screen === "tasks" || screen === "internal-tasks" || screen === "users" || screen === "connections" || screen === "billing") {
        setCurrentScreen({ type: screen });
      }
    };

    switch (currentScreen.type) {
      case "calendar":
        return <CalendarScreen user={auth.user} onLogout={auth.logout} onNavigate={handleNavigate} users={users} medicationTypes={medicationTypes} meetingTypes={meetingTypes} clinics={clinics} taskCount={taskActionCount} whatsappUnreadCount={whatsAppUnreadCount} />;
      case "patients":
        return <PatientsScreen user={auth.user} onLogout={auth.logout} onNavigate={handleNavigate} users={users} meetingTypes={meetingTypes} clinics={clinics} taskCount={taskActionCount} whatsappUnreadCount={whatsAppUnreadCount} />;
      case "adhoc-meetings":
        return <AdHocMeetingsScreen user={auth.user} onLogout={auth.logout} onNavigate={handleNavigate} users={users} meetingTypes={meetingTypes} clinics={clinics} taskCount={taskActionCount} whatsappUnreadCount={whatsAppUnreadCount} />;
      case "tasks":
        return <TasksScreen user={auth.user} onLogout={auth.logout} onNavigate={handleNavigate} medicationTypes={medicationTypes} users={users} meetingTypes={meetingTypes} diagnosisCodes={diagnosisCodes} clinics={clinics} taskCount={taskActionCount} whatsappUnreadCount={whatsAppUnreadCount} />;
      case "internal-tasks":
        return <InternalTasksScreen user={auth.user} onLogout={auth.logout} onNavigate={handleNavigate} users={users} meetingTypes={meetingTypes} clinics={clinics} taskCount={taskActionCount} whatsappUnreadCount={whatsAppUnreadCount} />;
      case "users":
        return <UsersScreen user={auth.user} onLogout={auth.logout} onNavigate={handleNavigate} users={users} onRefreshUsers={refetchUsers} taskCount={taskActionCount} whatsappUnreadCount={whatsAppUnreadCount} />;
      case "connections":
        return <ConnectionsScreen user={auth.user} onLogout={auth.logout} onNavigate={handleNavigate} taskCount={taskActionCount} whatsappUnreadCount={whatsAppUnreadCount} />;
      case "billing":
        return <BillingScreen user={auth.user} onLogout={auth.logout} onNavigate={handleNavigate} taskCount={taskActionCount} whatsappUnreadCount={whatsAppUnreadCount} />;
      case "ai-support":
        return <AISupportConsoleScreen user={auth.user} onLogout={auth.logout} onNavigate={handleNavigate} taskCount={taskActionCount} whatsappUnreadCount={whatsAppUnreadCount} />;
      case "whatsapp":
        return <WhatsAppScreen user={auth.user} users={users as any} onLogout={auth.logout} onNavigate={handleNavigate} taskCount={taskActionCount} whatsappUnreadCount={whatsAppUnreadCount} onClearUnreadCount={() => setWhatsAppUnreadCount(0)} phoneNumber={currentScreen.phone} patientId={currentScreen.patientId} />;
      case "patient":
        return <PatientPage user={auth.user} onLogout={auth.logout} onNavigate={handleNavigate} patientId={currentScreen.id} users={users} medicationTypes={medicationTypes} meetingTypes={meetingTypes} diagnosisCodes={diagnosisCodes} patientFileTypes={patientFileTypes} clinics={clinics} taskCount={taskActionCount} whatsappUnreadCount={whatsAppUnreadCount} />;
      default:
        return <PatientsScreen user={auth.user} onLogout={auth.logout} onNavigate={handleNavigate} users={users} meetingTypes={meetingTypes} clinics={clinics} taskCount={taskActionCount} whatsappUnreadCount={whatsAppUnreadCount} />;
    }
  };

  // Debug logging
  console.log("[App] Render:", {
    isGoogleCallback,
    isLoading: auth.isLoading,
    isAuthenticated: auth.isAuthenticated,
    hasUser: !!auth.user
  });

  return (
    <QueryClientProvider client={queryClient}>
      {isGoogleCallback ? (
        <GoogleCallbackScreen
          onLoginSuccess={() => {
            // After successful login, reload the page to reinitialize auth state
            window.location.href = "/";
          }}
          onLoginError={(error) => {
            console.error("Google login callback error:", error);
            // The redirect below is a full page load, so React state does not
            // survive it and the explanation vanishes after three seconds --
            // right when the person is still reading it. Hand it to the login
            // screen through sessionStorage, which does survive, rather than a
            // query param: the message can name a personal email address, and a
            // URL would put that in history, referrers and server logs.
            try {
              sessionStorage.setItem("loginError", error);
            } catch (e) {
              // Private mode can refuse storage. Losing the message is bad but
              // not worth blocking the redirect over.
            }
            // On error, redirect back to login
            setTimeout(() => {
              window.location.href = "/";
            }, 3000);
          }}
        />
      ) : (auth.isLoading && auth.isAuthenticated) || (auth.isAuthenticated && !auth.user) ? (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          backgroundColor: "#f9fafb"
        }}>
          <div style={{ textAlign: "center" }}>
            <div style={{
              width: 48,
              height: 48,
              border: "3px solid #e5e7eb",
              borderTop: "3px solid #3b5d6b",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto"
            }}></div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      ) : auth.isAuthenticated && auth.user ? (
        <NotificationsProvider
          isAuthenticated={auth.isAuthenticated}
          user={auth.user}
          refreshSignal={notificationRefreshSignal}
          onNavigate={(_screen, patientId) => setCurrentScreen({ type: "patient", id: patientId })}
        >
          <CallProvider>
            <AISupportProvider>
              {renderScreen()}
              <NotificationToast />
              <CallWidget />
              {/* Open to every signed-in user. The assistant answers only from
                  what the caller can already reach -- its tools resolve them and
                  apply the same patient-access rules the screens do -- so there
                  is nothing here for a per-user flag to protect. The support
                  console at /ai-support is a different matter and stays gated on
                  ai_support_admin in the sidebar. */}
              <AISupportWidget />
            </AISupportProvider>
          </CallProvider>
        </NotificationsProvider>
      ) : (
        <LoginScreen
          onLogin={auth.login}
          onGoogleLoginInit={auth.googleLoginInit}
          isLoading={auth.isLoading}
          error={auth.error}
          forgotPasswordKey={forgotPasswordKey}
        />
      )}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
